/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: StandartJackpot.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  27.09.22 2:37 pm.
 * Last modified: 27.09.22 2:37 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect } from 'react';
import { addBuildHash, updateResizables } from '../../api/helpers';
import { useCustomInView } from '../../hooks/useCustomInView';
import { useEffectWithRef } from '../../hooks/useEffectWithRef';
import useJackpotHoverPopup from '../../hooks/useJackpotHoverPopup';
import DefaultJackpotLevelValue from '../DefaultJackpotLevelValue';
import JackpotLevelValue from './JackpotLevelValue';

const getPaddingProps = (paddingProps: IStandardJackpotPaddingProps) => (
    jackpotType: 'grand' | 'major' | 'minor' | 'mini',
    side: 'left' | 'right' | 'top',
): any => {
    const paddingProp = paddingProps?.[side];
    if (paddingProp) {
        if (side === 'left' || side === 'right') {
            if (typeof paddingProp === 'number' || typeof paddingProp === 'string') {
                return {
                    ['data-padding-' + side + '-pixels']: paddingProp,
                };
            } else {
                if (paddingProp?.[jackpotType]) {
                    return {
                        ['data-padding-' + side + '-pixels']: paddingProp?.[jackpotType],
                    };
                }
            }
        } else {
            if (typeof paddingProp === 'number' || typeof paddingProp === 'string') {
                return {
                    'data-move-up-percent': paddingProp,
                };
            } else {
                if (paddingProp?.[jackpotType]) {
                    return {
                        'data-move-up-percent': paddingProp?.[jackpotType],
                    };
                }
            }
        }
    }

    return {};
};

const StandardJackpot = ({
    icons,
    values,
    defaultValues,
    currency,
    sectionName,
    padding,
    jackpotName,
    denomination,
    classes,
}: IStandardJackpotProps) => {
    const [setRefLevel1, refLevel1] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [setRefLevel2, refLevel2] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [setRefLevel3, refLevel3] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [setRefLevel4, refLevel4] = useEffectWithRef(node => {
        updateResizables(node, true);
    });
    const [jpContainerRef, isVisible] = useCustomInView();

    const overrideClasses = mergeClasses(classes || {});

    const getPaddingPropsFor = padding ? getPaddingProps(padding) : () => ({});

    useEffect(() => {
        if (isVisible) {
            if (refLevel1.current) {
                updateResizables(refLevel1.current, true);
            }
            if (refLevel2.current) {
                updateResizables(refLevel2.current, true);
            }
            if (refLevel3.current) {
                updateResizables(refLevel3.current, true);
            }
            if (refLevel4.current) {
                updateResizables(refLevel4.current, true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    const [hoverHandlers, HoverComponent] = useJackpotHoverPopup({ jackpotName, denomination, currency });

    return (
        <div
            className={`jp_std_values jp_std_values_${jackpotName.replaceAll(' ', '_').toLowerCase()}`}
            ref={jpContainerRef}
        >
            <div className="jp_std_icon">
                <img
                    className={`jp_std_icon_${jackpotName.replaceAll(' ', '_').toLowerCase()}`}
                    src={addBuildHash(icons.panel)}
                    data-ratio="0.2"
                    alt=""
                />
            </div>
            <div className="jp_std_container">
                <div className="jp_std_inside" {...hoverHandlers(1)}>
                    <img
                        src={addBuildHash(icons.grand)}
                        id={`std_level1_placeholder_${sectionName}`}
                        className={`std_level1_placeholder ${overrideClasses?.images?.grand}`}
                        alt=""
                        data-ratio="0.2"
                    />
                    <HoverComponent level={1} classes={['jp_std_popup_addition_1']} />
                    <div
                        className={`resizable ${overrideClasses?.resizables?.grand}`}
                        data-target={`std_level1_placeholder_${sectionName}`}
                        data-text-align="center"
                        ref={setRefLevel1}
                        {...getPaddingPropsFor('grand', 'left')}
                        {...getPaddingPropsFor('grand', 'right')}
                        {...getPaddingPropsFor('grand', 'top')}
                    >
                        <div
                            className={`jp_std_counter jp_std_numbers jp_std_level1 ${overrideClasses?.counters?.grand}`}
                        >
                            {typeof values.grand === 'string' && <span>{values.grand}</span>}
                            {typeof values.grand === 'object' && values.grand.default && (
                                <DefaultJackpotLevelValue value={values.grand.default} denomination={denomination} />
                            )}
                            {typeof values.grand === 'number' && (
                                <JackpotLevelValue
                                    jackpotName={jackpotName}
                                    denomination={denomination}
                                    level={values.grand}
                                    defaultValue={defaultValues?.grand}
                                />
                            )}
                            <span className="jp_std_currency">{currency}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="jp_std_container">
                <div className="jp_std_inside" {...hoverHandlers(2)}>
                    <img
                        src={addBuildHash(icons.major)}
                        id={`std_level2_placeholder_${sectionName}`}
                        className={`std_level2_placeholder ${overrideClasses?.images?.major}`}
                        alt=""
                        data-ratio="0.22"
                    />
                    <HoverComponent level={2} classes={['jp_std_popup_addition_2']} />
                    <div
                        className={`resizable ${overrideClasses?.resizables?.major}`}
                        data-target={`std_level2_placeholder_${sectionName}`}
                        data-text-align="center"
                        ref={setRefLevel2}
                        {...getPaddingPropsFor('major', 'left')}
                        {...getPaddingPropsFor('major', 'right')}
                        {...getPaddingPropsFor('major', 'top')}
                    >
                        <div
                            className={`jp_std_counter jp_std_numbers jp_std_level2 ${overrideClasses?.counters?.major}`}
                        >
                            {typeof values.major === 'string' && <span>{values.major}</span>}
                            {typeof values.major === 'object' && values.major.default && (
                                <DefaultJackpotLevelValue value={values.major.default} denomination={denomination} />
                            )}
                            {typeof values.major === 'number' && (
                                <JackpotLevelValue
                                    jackpotName={jackpotName}
                                    denomination={denomination}
                                    level={values.major}
                                    defaultValue={defaultValues?.major}
                                />
                            )}
                            <span className="jp_std_currency">{currency}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="jp_std_low_level_container">
                <div className="jp_std_inside" {...hoverHandlers(3)}>
                    <img
                        src={addBuildHash(icons.minor)}
                        id={`std_level3_placeholder_${sectionName}`}
                        className={`std_level3_placeholder ${overrideClasses?.images?.minor}`}
                        alt=""
                        data-ratio="0.23"
                    />
                    <HoverComponent level={3} classes={['jp_std_popup_offset_100']} arrowClass="egtd_jpw_hp_arrow_25" />
                    <div
                        className={`resizable ${overrideClasses?.resizables?.minor}`}
                        data-target={`std_level3_placeholder_${sectionName}`}
                        data-text-align="center"
                        ref={setRefLevel3}
                        {...getPaddingPropsFor('minor', 'left')}
                        {...getPaddingPropsFor('minor', 'right')}
                        {...getPaddingPropsFor('minor', 'top')}
                    >
                        <div
                            className={`jp_std_counter jp_std_numbers jp_std_level3 ${overrideClasses?.counters?.minor}`}
                        >
                            {typeof values.minor === 'string' && <span>{values.minor}</span>}
                            {typeof values.minor === 'object' && values.minor.default && (
                                <DefaultJackpotLevelValue value={values.minor.default} denomination={denomination} />
                            )}
                            {typeof values.minor === 'number' && (
                                <JackpotLevelValue
                                    jackpotName={jackpotName}
                                    denomination={denomination}
                                    level={values.minor}
                                    defaultValue={defaultValues?.minor}
                                />
                            )}
                            <span className="jp_std_currency">{currency}</span>
                        </div>
                    </div>
                </div>
                <div className="jp_std_inside" {...hoverHandlers(4)}>
                    <img
                        src={addBuildHash(icons.mini)}
                        id={`std_level4_placeholder_${sectionName}`}
                        className={`std_level4_placeholder ${overrideClasses?.images?.mini}`}
                        alt=""
                        data-ratio="0.23"
                    />
                    <HoverComponent level={4} classes={['jp_std_popup_offset_0']} arrowClass="egtd_jpw_hp_arrow_75" />
                    <div
                        className={`resizable ${overrideClasses?.resizables?.mini}`}
                        data-target={`std_level4_placeholder_${sectionName}`}
                        data-text-align="center"
                        ref={setRefLevel4}
                        {...getPaddingPropsFor('mini', 'left')}
                        {...getPaddingPropsFor('mini', 'right')}
                        {...getPaddingPropsFor('mini', 'top')}
                    >
                        <div
                            className={`jp_std_counter jp_std_numbers jp_std_level4 ${overrideClasses?.counters?.mini}`}
                        >
                            {typeof values.mini === 'string' && <span>{values.mini}</span>}
                            {typeof values.mini === 'object' && values.mini.default && (
                                <DefaultJackpotLevelValue value={values.mini.default} denomination={denomination} />
                            )}
                            {typeof values.mini === 'number' && (
                                <JackpotLevelValue
                                    jackpotName={jackpotName}
                                    denomination={denomination}
                                    level={values.mini}
                                    defaultValue={defaultValues?.mini}
                                />
                            )}
                            <span className="jp_std_currency">{currency}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface IStandardJackpotPaddingProps {
    left?:
        | {
              grand: string | number;
              major: string | number;
              minor: string | number;
              mini: string | number;
          }
        | number
        | string;
    right?:
        | {
              grand: string | number;
              major: string | number;
              minor: string | number;
              mini: string | number;
          }
        | number;
    top?:
        | {
              grand: string | number;
              major: string | number;
              minor: string | number;
              mini: string | number;
          }
        | number;
}

interface IStandardJackpotProps {
    icons: {
        panel: string;
        grand: string;
        major: string;
        minor: string;
        mini: string;
    };
    values: {
        grand: number | string | { default: number };
        major: number | string | { default: number };
        minor: number | string | { default: number };
        mini: number | string | { default: number };
    };
    defaultValues?: {
        grand?: number;
        major?: number;
        minor?: number;
        mini?: number;
    };
    padding?: IStandardJackpotPaddingProps;
    jackpotName: string;
    sectionName: string;
    denomination: number;
    classes?: IClasses;
    currency: string;
}

interface IClasses {
    images?: {
        grand?: string;
        major?: string;
        minor?: string;
        mini?: string;
    };
    resizables?: {
        grand?: string;
        major?: string;
        minor?: string;
        mini?: string;
    };
    counters?: {
        grand?: string;
        major?: string;
        minor?: string;
        mini?: string;
    };
}

const mergeClasses = (providedClasses: IClasses): IClasses => {
    const result: IClasses = {};
    type iClassesKey = keyof IClasses;

    Object.keys(defaultClasses).forEach((k: string) => {
        const key = k as iClassesKey;
        result[key] = { ...defaultClasses[key], ...(providedClasses[key] ? providedClasses[key] : {}) };
    });

    return result;
};

const defaultClasses = {
    images: {
        grand: '',
        major: '',
        minor: '',
        mini: '',
    },
    resizables: {
        grand: '',
        major: '',
        minor: '',
        mini: '',
    },
    counters: {
        grand: '',
        major: '',
        minor: '',
        mini: '',
    },
};

StandardJackpot.defaultProps = {
    classes: defaultClasses,
};

export default StandardJackpot;
